import type { BuildInfo } from './build'

/**
 * Whether the tab is running code the server has replaced.
 *
 * A tab that has been open since before a deploy keeps running what it loaded. Hosting
 * sends `no-cache` on the HTML, so anybody who reloads gets the new version — but nobody
 * reloads a tab that looks fine, which is how somebody spends an afternoon reporting bugs
 * that were fixed that morning.
 *
 * `build-info.json` is written at build time beside the bundle and is served with the same
 * no-cache header, so asking for it is asking the server what it is serving right now.
 */
export function isNewerBuild(running: BuildInfo, latest: BuildInfo | null): boolean {
  // A dirty tree means a local build, where the sha moves on every save and would report a
  // new version constantly.
  if (running.dirty) return false
  if (!latest || typeof latest.sha !== 'string' || latest.sha === '') return false
  return latest.sha !== running.sha || latest.version !== running.version
}

/**
 * The sentence under "A new version is available".
 *
 * It has to name the thing that actually changed. `version` comes from package.json and
 * is not bumped on every deploy, so naming only that produced "running v0.62.1; v0.62.1
 * is out" - a true statement that reads as a broken checker, which is worse than saying
 * nothing. When the versions match, the commit is what moved, so the commit is what it
 * says.
 */
export function updateNotice(running: BuildInfo, latest: BuildInfo | null): string {
  if (!latest) return `This tab is running v${running.version}.`
  if (latest.version !== running.version) {
    return `This tab is running v${running.version}; v${latest.version} is out.`
  }
  return `This tab is running v${running.version} (${running.sha}); ` +
    `the server is serving ${latest.sha}.`
}
